import type { DeepReadonly } from "@itandi/itandi-bb-ui";
import { Input } from "@itandi/itandi-bb-ui/Input";
import { InputSelect } from "@itandi/itandi-bb-ui/InputSelect";
import { useFormContext } from "react-hook-form";
import type { Prefecture } from "~/constants/PrefectureConst";
import { prefectures } from "~/constants/PrefectureConst";
import type { MarketCompanySetting } from "~/models/CompanySetting";
import Styles from "./Form.module.scss";

type Props = DeepReadonly<{
	isConfirm?: boolean;
}>;

// https://www.mhlw.go.jp/topics/2007/07/dl/tp0727-1d.pdf 通りに並べてください
const AVAILABLE_PREFECTURES = [
	"埼玉県",
	"千葉県",
	"東京都",
	"神奈川県",
	"愛知県",
	"京都府",
	"大阪府",
	"兵庫県",
	"福岡県"
];

const availablePrefectures = AVAILABLE_PREFECTURES.map((name) =>
	prefectures.find((p) => p.name === name)
).filter((p): p is Prefecture => p !== undefined);

export function CompanyStoreForm({ isConfirm = false }: Props) {
	const { register, getValues, formState } =
		useFormContext<MarketCompanySetting>();
	const values = getValues();
	const { errors } = formState;
	return (
		<div className={Styles.FormGroup}>
			<div className={Styles.GroupLabel}>店舗情報</div>
			<div className={Styles.FormList}>
				<div className={Styles.FormBlock}>
					<div className={Styles.FormLabel}>
						郵便番号
						{!isConfirm && <span className={Styles.RequireLabel}>必須</span>}
					</div>
					<div className={Styles.FormContent}>
						{isConfirm ? (
							<span>{values.zipcode}</span>
						) : (
							<Input
								isInvalid={!!errors.zipcode}
								placeholder="例）1234567"
								{...register("zipcode")}
							/>
						)}
						{errors.zipcode ? (
							<div className={Styles.ErrorMessage}>
								{errors.zipcode.message}
							</div>
						) : null}
					</div>
				</div>
				<div className={Styles.FormBlock}>
					<div className={Styles.FormLabel}>
						都道府県
						{!isConfirm && <span className={Styles.RequireLabel}>必須</span>}
					</div>
					<div className={Styles.FormContent}>
						{isConfirm ? (
							<span>{values.state}</span>
						) : (
							<div>
								<InputSelect {...register("state")}>
									<option value="">選択してください</option>
									{availablePrefectures.map((p) => (
										<option key={p.id} value={p.name}>
											{p.name}
										</option>
									))}
								</InputSelect>
							</div>
						)}
						{!isConfirm && (
							<p className={Styles.Description}>※エリアは順次拡大予定です。</p>
						)}
					</div>
				</div>
				<div className={Styles.FormBlock}>
					<div className={Styles.FormLabel}>
						市区町村・番地
						{!isConfirm && <span className={Styles.RequireLabel}>必須</span>}
					</div>
					<div className={Styles.FormContent}>
						{isConfirm ? (
							<span>{values.city}</span>
						) : (
							<Input
								isInvalid={!!errors.city}
								placeholder="例）港区3丁目2-1"
								{...register("city")}
							/>
						)}
						{errors.city ? (
							<div className={Styles.ErrorMessage}>{errors.city.message}</div>
						) : null}
					</div>
				</div>
				<div className={Styles.FormBlock}>
					<div className={Styles.FormLabel}>
						建物・部屋番号
						{!isConfirm && <span className={Styles.OptionalLabel}>任意</span>}
					</div>
					<div className={Styles.FormContent}>
						{isConfirm ? (
							<span>{values.otherAddress}</span>
						) : (
							<Input
								isInvalid={!!errors.otherAddress}
								placeholder="例）六本木グランドタワー40階"
								{...register("otherAddress")}
							/>
						)}
						{errors.otherAddress ? (
							<div className={Styles.ErrorMessage}>
								{errors.otherAddress.message}
							</div>
						) : null}
					</div>
				</div>
				<div className={Styles.FormBlock}>
					<div className={Styles.FormLabel}>
						店舗の代表電話番号
						{!isConfirm && <span className={Styles.RequireLabel}>必須</span>}
					</div>
					<div className={Styles.FormContent}>
						{isConfirm ? (
							<span>{values.phoneNumber}</span>
						) : (
							<Input
								isInvalid={!!errors.phoneNumber}
								placeholder="例）06-0000-0000"
								{...register("phoneNumber")}
							/>
						)}
						{errors.phoneNumber ? (
							<div className={Styles.ErrorMessage}>
								{errors.phoneNumber.message}
							</div>
						) : null}
					</div>
				</div>
				<div className={Styles.FormBlock}>
					<div className={Styles.FormLabel}>
						通知用メールアドレス
						{!isConfirm && <span className={Styles.RequireLabel}>必須</span>}
					</div>
					<div className={Styles.FormContent}>
						{isConfirm ? (
							<span>{values.email}</span>
						) : (
							<Input
								isInvalid={!!errors.email}
								placeholder="例）notification@example.com"
								{...register("email")}
							/>
						)}
						{errors.email ? (
							<div className={Styles.ErrorMessage}>{errors.email.message}</div>
						) : null}
					</div>
				</div>
			</div>
		</div>
	);
}
